import { T, tt } from '@agos/shared'
import { Lock } from 'lucide-react'
import type { ReactNode } from 'react'

import type { RouteGuard } from '../module-contract'
import { accessRefusal, useAccessContext, type AccessRefusal } from './access'

/**
 * The route's half of the gate — the half legacy never had, and the half this port had declared without
 * enforcing.
 *
 * `ModuleRoute.guard` describes what a route requires, and until now nothing read it on the routing side:
 * `canAccess` was wired to the sidebar only, so every ported page was reachable by URL by anyone signed in.
 * The server refuses the data, so no employee could *read* anything they should not — but the guards were
 * decorative, and every page spec claiming "the route admits whoever the server lets read" was describing
 * an intention rather than the behaviour. This closes that.
 *
 * **What a refusal shows.** Legacy renders the 404 page: a page that exists, that you are simply not
 * allowed to open, reported as one that does not exist. That is what makes FB-001 so hard to diagnose from
 * the outside — the sidebar offers *Všeobecné nastavenia*, the click gives *page not found*, and nothing
 * connects the two. Here the refusal says which gate closed and what it wanted, so the answer to *why can I
 * see the link but not the page* is on the screen instead of in the router source.
 *
 * **What it does not do** is invent gates. A route with no `guard` stays open — `/dashboard`, `/kitchen`
 * and `/lunch-menu` are unguarded in legacy too (FB-002), and closing them would lock cooks out of the
 * board on cutover day.
 */

function roleName(role: string): string {
  if (role === 'main_admin') return tt('hlavný administrátor')
  if (role === 'admin') return tt('administrátor')
  if (role === 'cook') return tt('kuchár')
  if (role === 'waiter') return tt('obsluha')
  if (role === 'driver') return tt('šofér')
  return role
}

function refusalTitle(refusal: AccessRefusal): string {
  if (refusal.gate === 'super-admin') return tt('Táto stránka je len pre správcu platformy')
  if (refusal.gate === 'role') return tt('Táto stránka nie je pre vašu rolu')
  if (refusal.gate === 'franchise-module') return tt('Tento modul nie je v balíku tejto franšízy')
  return tt('Na túto stránku nemáte právo')
}

function refusalDetail(refusal: AccessRefusal, role: string | undefined): string {
  if (refusal.gate === 'role') {
    const admitted = refusal.wanted.map((entry) => roleName(entry)).join(', ')
    const mine = role === undefined ? tt('neznáma rola') : roleName(role)
    return `${tt('Otvoriť ju môže')}: ${admitted}. ${tt('Vaša rola')}: ${mine}.`
  }
  if (refusal.gate === 'permission') {
    // Named, because "ask an administrator" is not actionable and the flag is what they would grant.
    const wanted = refusal.wanted.join(tt(' alebo '))
    return `${tt('Chýba právo')}: ${wanted}. ${tt('Prideliť ho môže hlavný administrátor v Právach.')}`
  }
  if (refusal.gate === 'franchise-module') {
    return `${tt('Modul')}: ${refusal.wanted.join(', ')}. ${tt('O balíku rozhoduje centrála.')}`
  }
  return tt('Prihláste sa ako správca platformy.')
}

export function RouteGate({ guard, children }: { guard?: RouteGuard; children: ReactNode }) {
  const access = useAccessContext()
  const refusal = accessRefusal(guard, access)

  if (refusal === null) return <>{children}</>

  return (
    <div
      className="max-w-xl mx-auto mt-10 rounded-2xl border border-border bg-card px-6 py-8 text-center"
      data-route-refused={refusal.gate}
    >
      <Lock className="w-8 h-8 text-muted-foreground mx-auto" />
      <p className="mt-4 text-base font-bold text-foreground">{refusalTitle(refusal)}</p>
      <p className="mt-2 text-sm text-muted-foreground">{refusalDetail(refusal, access.systemRole)}</p>
      <p className="mt-4 text-[11px] text-muted-foreground">
        {/* The distinction legacy's 404 destroyed. */}
        <T>Stránka existuje - len ju nemáte otvorenú vy.</T>
      </p>
    </div>
  )
}
